import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  App,
  Button,
  Card,
  Descriptions,
  Form,
  Modal,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import type { TableColumnsType } from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import CustomerForm from '../components/customers/CustomerForm';
import CustomerProductPricesTab from '../components/customers/CustomerProductPricesTab';
import { applyCustomerSaveError } from '../components/customers/customerFormErrors';
import {
  canCreateCustomers as canCreateCustomersForUser,
  canEditCustomers as canEditCustomersForUser,
  canListCustomers as canListCustomersForUser,
} from '../modules/customers/customerAccessPolicy';
import { createCustomer } from '../modules/customers/api/customersApi';
import type { Customer, CustomerSubmitValues } from '../modules/customers/types';
import { normalizeCustomerContacts } from '../utils/customerContacts';
import { DEFAULT_CUSTOMER_PAYMENT_TERM_DAYS } from '../modules/orders/customerPayment';
import { getVatState } from '../utils/customerVatStatus';

const { Text, Title } = Typography;

const EMPTY_VALUE = '—';

interface CustomerOrderSummary {
  id: string;
  orderNumber?: string;
  status?: string;
  createdAt?: string;
}

type CustomerDetailData = Customer & { orders?: CustomerOrderSummary[] };

const formatDateTime = (value?: string) => (value ? new Date(value).toLocaleString('pl-PL') : EMPTY_VALUE);

const CustomerDetail: React.FC = () => {
  const { t } = useTranslation(['customers', 'common', 'orders']);
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [customer, setCustomer] = useState<CustomerDetailData | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [creatingBranch, setCreatingBranch] = useState(false);
  const [branchSubmitting, setBranchSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [branchForm] = Form.useForm();

  const currentUser = authService.getCurrentUser();
  const canCreateCustomers = canCreateCustomersForUser(currentUser);
  const canEditCustomers = canEditCustomersForUser(currentUser);
  const canListCustomers = canListCustomersForUser(currentUser);
  const canViewCustomerPayments = authService.canViewCustomerPayments();
  const canViewCustomerSalePrices = authService.canViewCustomerSalePrices();
  const canEditCustomerSalePrices = authService.canEditCustomerSalePrices();

  const fetchCustomer = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [detailResponse, listResponse] = await Promise.all([
        api.get(`/customers/${id}`),
        canListCustomers
          ? api.get('/customers?status=all')
          : Promise.resolve({ data: [] })
      ]);
      setCustomer(detailResponse.data);
      setCustomers(listResponse.data);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        message.warning(t('customers:messages.notFound'));
        navigate('/app/customers', { replace: true });
        return;
      }
      message.error(t('customers:messages.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const parentCustomer = useMemo(
    () =>
      customer?.parentCustomerId
        ? customers.find((item) => item.id === customer.parentCustomerId) || null
        : null,
    [customer, customers]
  );

  const contacts = useMemo(
    () => (customer ? normalizeCustomerContacts(customer.contactPeople, customer.contactPerson, customer.phone) : []),
    [customer]
  );

  const branches = useMemo(
    () =>
      customer
        ? customers
            .filter((item) => item.parentCustomerId === customer.id && !item.deletedAt)
            .sort((left, right) =>
              left.name.localeCompare(right.name, 'pl', { sensitivity: 'base' })
            )
        : [],
    [customer, customers]
  );

  const handleUpdate = async (values: CustomerSubmitValues) => {
    if (!id) return;
    setSubmitting(true);
    form.setFields([
      { name: 'name', errors: [] },
      { name: 'contactPeople', errors: [] },
      { name: 'taxId', errors: [] },
      { name: 'regon', errors: [] },
      { name: 'parentCustomerId', errors: [] }
    ]);

    try {
      await api.put(`/customers/${id}`, values);
      message.success(t('customers:messages.updateSuccess'));
      setEditing(false);
      await fetchCustomer();
    } catch (error: any) {
      applyCustomerSaveError(error, form, t, message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateBranch = async (values: CustomerSubmitValues) => {
    setBranchSubmitting(true);
    branchForm.setFields([
      { name: 'name', errors: [] },
      { name: 'contactPeople', errors: [] },
      { name: 'taxId', errors: [] },
      { name: 'regon', errors: [] },
      { name: 'parentCustomerId', errors: [] }
    ]);

    try {
      const createdBranch = await createCustomer(values);
      message.success(t('customers:messages.createSuccess'));
      setCreatingBranch(false);
      branchForm.resetFields();

      if (createdBranch?.id) {
        navigate(`/app/customers/${createdBranch.id}`);
      } else {
        await fetchCustomer();
      }
    } catch (error: any) {
      applyCustomerSaveError(error, branchForm, t, message);
    } finally {
      setBranchSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!customer) {
    return null;
  }

  const renderValue = (value?: string | null) =>
    value ? value : <Text type="secondary">{EMPTY_VALUE}</Text>;

  const renderVatStatus = () => {
    const state = getVatState(customer);
    if (state === 'unchecked') {
      return <Text type="secondary">{t('customers:validation.notChecked')}</Text>;
    }
    return state === 'valid' ? (
      <Tag color="success" icon={<CheckCircleOutlined />}>
        {t('customers:table.valid')}
      </Tag>
    ) : (
      <Tag color="error" icon={<CloseCircleOutlined />}>
        {t('customers:table.invalid')}
      </Tag>
    );
  };

  const orderColumns: TableColumnsType<CustomerOrderSummary> = [
    {
      title: t('orders:table.orderNumber'),
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      render: (value: string | undefined, record) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/app/orders/${record.id}`)}>
          {value || record.id}
        </Button>
      )
    },
    {
      title: t('orders:table.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string | undefined) =>
        status ? <Tag>{t(`orders:statusOptions.${status}`)}</Tag> : EMPTY_VALUE
    },
    {
      title: t('customers:preview.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string | undefined) => formatDateTime(value)
    }
  ];

  const dataTab = editing ? (
    <Card>
      <CustomerForm
        form={form}
        customers={customers}
        editingCustomer={customer}
        canViewCustomerPayments={canViewCustomerPayments}
        submitting={submitting}
        submitLabel={t('common:save')}
        onSubmit={handleUpdate}
        open={editing}
        onCancel={() => setEditing(false)}
      />
    </Card>
  ) : (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {canEditCustomers && !customer.deletedAt && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
            {t('common:edit')}
          </Button>
        </div>
      )}

      <Card title={t('customers:preview.companySection')}>
        <Descriptions column={1} size="small">
          <Descriptions.Item label={t('customers:preview.customerType')}>
            <Tag bordered={false}>
              {customer.isBranch ? t('customers:preview.branch') : t('customers:preview.company')}
            </Tag>
          </Descriptions.Item>
          {customer.isBranch && (
            <Descriptions.Item label={t('customers:preview.parentCustomer')}>
              {parentCustomer ? (
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => navigate(`/app/customers/${parentCustomer.id}`)}
                >
                  {parentCustomer.name}
                </Button>
              ) : (
                renderValue(null)
              )}
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t('customers:preview.taxId')}>
            {renderValue(customer.taxId)}
          </Descriptions.Item>
          <Descriptions.Item label={t('customers:preview.regon')}>
            {renderValue(customer.regon)}
          </Descriptions.Item>
          <Descriptions.Item label={t('customers:preview.vatStatus')}>{renderVatStatus()}</Descriptions.Item>
          <Descriptions.Item label={t('customers:preview.address')}>
            {renderValue(customer.address)}
          </Descriptions.Item>
          {canViewCustomerPayments && (
            <Descriptions.Item label={t('customers:form.defaultPaymentTermDays')}>
              {t('customers:preview.paymentTermDays', {
                count: customer.defaultPaymentTermDays ?? DEFAULT_CUSTOMER_PAYMENT_TERM_DAYS
              })}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card title={t('customers:preview.contactSection')}>
        <Descriptions column={1} size="small">
          <Descriptions.Item label={t('customers:preview.contactPeople')}>
            {contacts.length > 0 ? (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {contacts.map((contact, index) => (
                  <div key={`${customer.id}-contact-${index}`}>
                    <Text strong>{contact.name || EMPTY_VALUE}</Text>
                    {contact.phone ? ` · ${contact.phone}` : ''}
                  </div>
                ))}
              </Space>
            ) : (
              renderValue(null)
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('customers:preview.contactEmail')}>
            {renderValue(customer.email)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {!customer.isBranch && (
        <Card
          title={t('customers:detail.branchesTitle')}
          extra={
            canCreateCustomers && !customer.deletedAt ? (
              <Tooltip
                title={!customer.taxId ? t('customers:detail.branchRequiresTaxId') : undefined}
              >
                <span>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={!customer.taxId}
                    onClick={() => setCreatingBranch(true)}
                  >
                    {t('customers:detail.addBranch')}
                  </Button>
                </span>
              </Tooltip>
            ) : null
          }
        >
          {branches.length > 0 ? (
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {branches.map((branch) => (
                <Space key={branch.id} size={8} wrap>
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => navigate(`/app/customers/${branch.id}`)}
                  >
                    {branch.name}
                  </Button>
                </Space>
              ))}
            </Space>
          ) : (
            <Text type="secondary">{t('customers:detail.noBranches')}</Text>
          )}
        </Card>
      )}

      <Card>
        <Descriptions column={1} size="small">
          <Descriptions.Item label={t('customers:preview.createdAt')}>
            {formatDateTime(customer.createdAt)}
          </Descriptions.Item>
          <Descriptions.Item label={t('customers:preview.updatedAt')}>
            {formatDateTime(customer.updatedAt)}
          </Descriptions.Item>
          {customer.deletedAt && (
            <Descriptions.Item label={t('customers:preview.archivedAt')}>
              {formatDateTime(customer.deletedAt)}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>
    </Space>
  );

  const tabItems = [
    { key: 'data', label: t('customers:detail.dataTab'), children: dataTab },
    {
      key: 'orders',
      label: t('customers:detail.ordersTab'),
      children: (
        <Table<CustomerOrderSummary>
          columns={orderColumns}
          dataSource={customer.orders || []}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: t('customers:detail.noOrders') }}
        />
      )
    },
    ...(canViewCustomerSalePrices
      ? [{
          key: 'prices',
          label: t('customers:detail.pricesTab'),
          children: <CustomerProductPricesTab customerId={customer.id} canEdit={canEditCustomerSalePrices} />
        }]
      : [])
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/app/customers')}>
            {t('common:back')}
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            {customer.name}
          </Title>
          {customer.isBranch && <Tag bordered={false}>{t('customers:preview.branch')}</Tag>}
          {customer.deletedAt && <Tag color="default">{t('customers:views.archivedShort')}</Tag>}
        </div>

        {customer.deletedAt && (
          <Text type="secondary">{t('customers:preview.archivedNotice')}</Text>
        )}

        <Tabs items={tabItems} />
      </Space>

      <Modal
        title={t('customers:detail.createBranchTitle', { name: customer.name })}
        open={creatingBranch}
        onCancel={() => setCreatingBranch(false)}
        maskClosable={false}
        footer={null}
        width={600}
        destroyOnClose
      >
        <CustomerForm
          form={branchForm}
          customers={customers}
          editingCustomer={null}
          initialBranchParent={customer}
          canViewCustomerPayments={canViewCustomerPayments}
          submitting={branchSubmitting}
          submitLabel={t('customers:detail.addBranch')}
          onSubmit={handleCreateBranch}
          open={creatingBranch}
          onCancel={() => setCreatingBranch(false)}
        />
      </Modal>
    </div>
  );
};

export default CustomerDetail;
